import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UserAuthContext = createContext(null);

export const useUserAuth = () => {
  const context = useContext(UserAuthContext);
  if (!context) {
    throw new Error('useUserAuth must be used within UserAuthProvider');
  }
  return context;
};

// Helper to format API error messages
const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const UserAuthProvider = ({ children }) => {
  // null = checking, false = not authenticated, object = authenticated user
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is authenticated on mount
  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Register new user
  const register = async (email, password, name, role = 'student', phone = null, preferredLanguage = 'en') => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/register`,
        { email, password, name, role, phone, preferred_language: preferredLanguage },
        { withCredentials: true }
      );
      setUser(response.data.user);
      return { success: true, user: response.data.user };
    } catch (error) {
      const errorMessage = formatApiErrorDetail(error.response?.data?.detail);
      return { success: false, error: errorMessage };
    }
  };

  // Login user
  const login = async (email, password) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(response.data.user);
      return { success: true, user: response.data.user };
    } catch (error) {
      const errorMessage = formatApiErrorDetail(error.response?.data?.detail);
      return { success: false, error: errorMessage };
    }
  };

  // Logout user
  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      // Logout API failed - still clear local state
    } finally {
      setUser(false);
    }
  };

  // Forgot password
  const forgotPassword = async (email) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/forgot-password`,
        { email },
        { withCredentials: true }
      );
      return { success: true };
    } catch (error) {
      const errorMessage = formatApiErrorDetail(error.response?.data?.detail);
      return { success: false, error: errorMessage };
    }
  };

  // Reset password
  const resetPassword = async (token, newPassword) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/reset-password`,
        { token, new_password: newPassword },
        { withCredentials: true }
      );
      return { success: true };
    } catch (error) {
      const errorMessage = formatApiErrorDetail(error.response?.data?.detail);
      return { success: false, error: errorMessage };
    }
  };

  // Change password
  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { withCredentials: true }
      );
      return { success: true };
    } catch (error) {
      const errorMessage = formatApiErrorDetail(error.response?.data?.detail);
      return { success: false, error: errorMessage };
    }
  };

  // Update profile
  const updateProfile = async (data) => {
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/auth/profile`,
        data,
        { withCredentials: true }
      );
      setUser(response.data.user);
      return { success: true, user: response.data.user };
    } catch (error) {
      const errorMessage = formatApiErrorDetail(error.response?.data?.detail);
      return { success: false, error: errorMessage };
    }
  };

  // Refresh token
  const refreshToken = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/refresh`, {}, { withCredentials: true });
      return { success: true };
    } catch (error) {
      setUser(false);
      return { success: false };
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isStudent: user?.role === 'student',
    isTeacher: user?.role === 'teacher',
    register,
    login,
    logout,
    forgotPassword,
    resetPassword,
    changePassword,
    updateProfile,
    refreshToken,
    checkAuth
  };

  return (
    <UserAuthContext.Provider value={value}>
      {children}
    </UserAuthContext.Provider>
  );
};

export default UserAuthContext;
